import React from "react";
import { useSelector } from "react-redux";
import { Select, Space, TreeSelect, Tag, ConfigProvider, Button } from "antd";
import { UserOutlined } from "@ant-design/icons";
import Icon from "@ant-design/icons";
import { CloseSvg } from "../../assets/icons";
import { ArrowDownIcon, SearchIcon } from "./userTableIcons.jsx";

const { SHOW_CHILD } = TreeSelect;
import {
  ActionButton,
  CustomCheckbox,
} from "../../styles/UserManagement.styled";
import { regionMarketData as staticRegionMarketData } from "./useUserData";
import { COUNTRY_CODE_TO_NAME } from "../../components/MarketOverviewPage/data/constants";
import { MODULE_SCOPES } from "../../constants/moduleScopes";
import { getCallerMarketScope } from "../../utils/callerMarketScope";
import { canAssignRole, roleUnavailableReason } from "../../utils/roleUtils";

// Reverse map: full country name → country code
const NAME_TO_CODE = Object.fromEntries(
  Object.entries(COUNTRY_CODE_TO_NAME).map(([code, name]) => [name, code])
);
const toCode = (nameOrCode) => NAME_TO_CODE[nameOrCode] || nameOrCode;

// Role choices for the inline Role editor, highest privilege first. Mirrors the
// invite modal's ROLE_OPTIONS so the two role editors read alike; which of them
// a given caller may actually pick comes from the server (`assignable_roles`).
const ROLE_DROPDOWN_OPTIONS = [
  {
    value: "super_admin",
    label: "Super Admin",
    description: "Super user has access to manage all market.",
  },
  {
    value: "admin",
    label: "Admin",
    description: "Admin has access to manage the assigned market.",
  },
  {
    value: "normal_user",
    label: "Normal User",
    description: "Normal user can view the scope of assigned market.",
  },
];

export const useUserTableColumns = ({
  editingRows,
  openDropdowns,
  stagingData,
  getRoleTagOption,
  commonButtonStyles,
  handleRoleDropdownOpenChange,
  handleProductDropdownOpenChange,
  handleSaveUser,
  handleStageChange,
  startEditUser,
  stopEditUser,
  handleAddModule,
  handleDeleteUser,
  handleResendInvitation,
  handleResetPassword, // Per-row workspace-admin password reset
  canResetPassword, // Cosmetic role gate for the Reset action (record) => bool
  handleReactivateUser, // Deactivated-view row action
  handlePermanentDelete, // Deactivated-view row action
  isReactivating,
  isSaving,
  users, // Add users data to calculate rowSpans
  allUsers, // Full user list for module count / product exclusion checks
  calculateRowSpans, // Add rowSpan calculation function
  regionMarketData: dynamicRegionMarketData, // Dynamic region-market data from Redux
  assignableRoles, // Server-computed grant ladder; null until the roster loads
  currentUserEmail, // Email of the logged-in user — their row has no Edit/Delete
}) => {
  // Use dynamic data if provided, otherwise fall back to static data
  const regionMarketData =
    dynamicRegionMarketData && Object.keys(dynamicRegionMarketData).length > 0
      ? dynamicRegionMarketData
      : staticRegionMarketData;

  // A market-scoped admin caller may only manage their own markets, so the
  // Market picker options are limited to that set (null = unrestricted, i.e.
  // Dashmote staff / workspace super_admin). The backend merge-preserve clamp
  // enforces this regardless — this is UX / defense-in-depth. A target's
  // pre-existing out-of-scope markets still render as selected tags (the
  // TreeSelect value) and are preserved server-side on save.
  const { userData, selectedWorkspace } = useSelector((state) => state.auth);
  const callerMarketScope = getCallerMarketScope(
    userData,
    selectedWorkspace?.key || selectedWorkspace
  );

  // allUsers falls back to users when not provided (user list view)
  const userPool = allUsers || users;

  // Calculate rowSpans for cell merging
  const rowSpans = calculateRowSpans ? calculateRowSpans(users) : {};

  // Format the soft-delete timestamp for the deactivated-row status line
  // (e.g. "15 Jul 2026"). Defensive: missing / unparseable → no date shown.
  const formatDeactivatedOn = (value) => {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime())
      ? null
      : d.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        });
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "15rem",
      onCell: (record) => {
        const span = rowSpans[`${record.key}-name`];
        return {
          rowSpan: span || (calculateRowSpans ? 0 : 1), // Use 1 if no calculateRowSpans function
        };
      },
      render: (text, record) => {
        // Only render content for rows that have rowSpan > 0, or if no calculateRowSpans function
        const span = rowSpans[`${record.key}-name`];
        if (calculateRowSpans && (!span || span === 0)) {
          return null;
        }

        const isSelf = currentUserEmail && record.email === currentUserEmail;
        const isPending = record.invitation_status === "pending_activation";
        const isDeactivated = record.isDeactivated;
        const deactivatedOn = formatDeactivatedOn(record.delete_time);
        const blue = { ...commonButtonStyles, color: "#0e49b5" };
        const red = { ...commonButtonStyles, color: "#df0101" };
        // For a user in more than one workspace, this row action removes only
        // THIS workspace's membership (handleDeleteUser → deleteType
        // "workspace"), not a global deactivation — so label it for what it
        // does. Only their last workspace resolves to a true Deactivate.
        const deactivateActionLabel =
          (record.workspaceCount ?? 1) > 1
            ? "Remove from Workspace"
            : "Deactivate";

        return (
          <Space align="start">
            <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center">
              {record.avatar ? (
                <img
                  src={record.avatar}
                  alt={text}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gray-300 rounded-full flex items-center justify-center">
                  <UserOutlined className="text-gray-600" />
                </div>
              )}
            </div>
            <div>
              <div className="font-medium">{text}</div>
              <div
                className="text-sm text-gray-500"
                style={{ marginTop: "2px" }}
              >
                {record.email}
              </div>
              {/*
                Status + per-user lifecycle actions (at most two), inline — no
                overflow menu. Each action renders only when its handler is
                supplied, so the Market-view tab (which passes a reduced handler
                set) degrades exactly as it did with the old Action column.
              */}
              <div style={{ marginTop: "4px" }}>
                {/* Status on its own line, above the action buttons. paddingTop
                    (not marginTop, which would collapse with the wrapper's own
                    top margin and do nothing) drops the pill off the email above
                    it — without it the pill (a full 22px tall) crowds upward and
                    reads as misaligned, most visibly on the wider gold "Pending
                    invite" pill. */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    paddingTop: "4px",
                  }}
                >
                  {isDeactivated ? (
                    deactivatedOn && (
                      <span style={{ fontSize: "0.72rem", color: "#71717a" }}>
                        Deactivated {deactivatedOn}
                      </span>
                    )
                  ) : isPending ? (
                    <Tag
                      color="gold"
                      style={{ borderRadius: "9999px", marginInlineEnd: 0 }}
                    >
                      Pending invite
                    </Tag>
                  ) : (
                    <Tag
                      color="green"
                      style={{ borderRadius: "9999px", marginInlineEnd: 0 }}
                    >
                      Active
                    </Tag>
                  )}
                </div>

                {/* Lifecycle actions grouped on one row so the two buttons
                    (e.g. Deactivate + Reset Password) never wrap apart from each
                    other, and never share a line with the status tag. */}
                {!isSelf && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      marginTop: "4px",
                      flexWrap: "wrap",
                      // AntD's text-button padding-inline (15px) would
                      // otherwise indent "Deactivate"/"Reactivate" past the
                      // email and status pill above — pull the row back so
                      // the first button's text lines up with them.
                      marginLeft: "-15px",
                    }}
                  >
                    {isDeactivated ? (
                      <>
                        {handleReactivateUser && (
                          <ActionButton
                            type="text"
                            loading={isReactivating}
                            onClick={() => handleReactivateUser(record)}
                            style={blue}
                          >
                            Reactivate
                          </ActionButton>
                        )}
                        {handlePermanentDelete && (
                          <ActionButton
                            type="text"
                            onClick={() => handlePermanentDelete(record)}
                            style={red}
                          >
                            Delete Permanently
                          </ActionButton>
                        )}
                      </>
                    ) : isPending ? (
                      <>
                        {handleResendInvitation && (
                          <ActionButton
                            type="text"
                            onClick={() => handleResendInvitation(record)}
                            style={blue}
                          >
                            Resend Invitation
                          </ActionButton>
                        )}
                        {handleDeleteUser && (
                          <ActionButton
                            type="text"
                            onClick={() => handleDeleteUser(record)}
                            style={blue}
                          >
                            {deactivateActionLabel}
                          </ActionButton>
                        )}
                      </>
                    ) : (
                      <>
                        {/* Routine action first, state-changing action last —
                            matches the Pending (Resend, then Deactivate) and
                            Deactivated (Reactivate, then Delete Permanently)
                            rows' ordering. */}
                        {handleResetPassword && canResetPassword?.(record) && (
                          <ActionButton
                            type="text"
                            onClick={() => handleResetPassword(record)}
                            style={blue}
                          >
                            Reset Password
                          </ActionButton>
                        )}
                        {handleDeleteUser && (
                          <ActionButton
                            type="text"
                            onClick={() => handleDeleteUser(record)}
                            style={blue}
                          >
                            {deactivateActionLabel}
                          </ActionButton>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </Space>
        );
      },
    },
    {
      title: "Role",
      dataIndex: "roleName",
      key: "role",
      width: "8rem",
      onCell: (record) => {
        const span = rowSpans[`${record.key}-role`];
        return {
          rowSpan: span || (calculateRowSpans ? 0 : 1), // Use 1 if no calculateRowSpans function
        };
      },
      render: (roleName, record) => {
        // Only render content for rows that have rowSpan > 0, or if no calculateRowSpans function
        const span = rowSpans[`${record.key}-role`];
        if (calculateRowSpans && (!span || span === 0)) {
          return null;
        }

        const isEditing = editingRows.has(record.key);
        const stagedRole = stagingData[record.key]?.role;
        const displayValue = stagedRole || roleName;

        if (isEditing) {
          return (
            <Select
              value={displayValue}
              open={openDropdowns.has(`role-${record.key}`)}
              onOpenChange={(open) =>
                handleRoleDropdownOpenChange(record, open)
              }
              style={{
                width: "100%",
                borderRadius: "9999px",
                height: "36px",
              }}
              onChange={(value) => handleStageChange(record, "role", value)}
              styles={{
                popup: {
                  root: {
                    borderRadius: "1.5rem",
                    padding: "0.75rem",
                    minWidth: "350px",
                  },
                },
              }}
              suffixIcon={<ArrowDownIcon />}
              labelRender={({ value }) => getRoleTagOption(value).label}
              popupRender={() => (
                <div>
                  {ROLE_DROPDOWN_OPTIONS.map((opt) => {
                    // Gated on the server-computed ladder, exactly like the
                    // invite RolePicker. Disabled rather than hidden, so the
                    // capability stays discoverable, and never silently
                    // selectable — the API would refuse it.
                    const disabled = !canAssignRole(opt.value, assignableRoles);
                    return (
                      <div
                        key={opt.value}
                        className={`px-4 py-2.5 rounded-full transition-colors ${
                          disabled
                            ? "cursor-not-allowed opacity-60"
                            : "hover:bg-gray-50 cursor-pointer"
                        }`}
                        onClick={() => {
                          if (disabled) return;
                          handleStageChange(record, "role", opt.value);
                          handleRoleDropdownOpenChange(record, false);
                        }}
                      >
                        <div className="mb-1">
                          {getRoleTagOption(opt.value).label}
                        </div>
                        <div className="text-[0.625rem] text-gray-600 ml-1">
                          {disabled
                            ? roleUnavailableReason(opt.value, assignableRoles)
                            : opt.description}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            >
              {ROLE_DROPDOWN_OPTIONS.map((opt) => (
                <Select.Option
                  key={opt.value}
                  value={opt.value}
                  disabled={!canAssignRole(opt.value, assignableRoles)}
                >
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          );
        }

        // Non-editing: the role tag plus an Edit affordance, so role is editable
        // from its own column and not only via the Market column's Edit button.
        // Same entry point (startEditUser → row edit mode) and gating as Market:
        // hidden on the caller's own row and on deactivated rows.
        const showRoleEdit =
          startEditUser &&
          !record.isDeactivated &&
          !(currentUserEmail && record.email === currentUserEmail);
        return (
          <div style={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
            {getRoleTagOption(roleName).label}
            {showRoleEdit && (
              <ActionButton
                type="text"
                onClick={() => startEditUser(record)}
                style={{
                  ...commonButtonStyles,
                  color: "#0e49b5",
                  height: "auto",
                  padding: 0,
                  border: "none",
                  lineHeight: "20px",
                }}
              >
                Edit
              </ActionButton>
            )}
          </div>
        );
      },
    },
    {
      title: "Product",
      dataIndex: "productName",
      key: "product",
      width: "10rem",
      render: (productName, record) => {
        const isEditing = editingRows.has(record.key);
        const stagedProduct = stagingData[record.key]?.product;
        const displayValue = stagedProduct || productName;

        if (isEditing) {
          // Exclude modules already assigned to other rows of the same user
          const otherRowModules = userPool
            .filter((u) => u.userId === record.userId && u.key !== record.key)
            .map((u) => u.productName);
          const productOptions = MODULE_SCOPES.map((m) => ({
            value: m.label,
            label: m.label,
          })).filter((opt) => !otherRowModules.includes(opt.value));

          return (
            <Select
              value={displayValue}
              open={openDropdowns.has(`product-${record.key}`)}
              onOpenChange={(open) =>
                handleProductDropdownOpenChange(record, open)
              }
              style={{
                width: "100%",
                borderRadius: "9999px",
                height: "36px",
              }}
              onChange={(value) => {
                handleStageChange(record, "product", value);
                handleProductDropdownOpenChange(record, false);
              }}
              styles={{
                popup: {
                  root: {
                    borderRadius: "24px",
                    padding: "4px 12px",
                    fontFamily: "Inter",
                    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
                    minWidth: "240px",
                  },
                },
              }}
              popupMatchSelectWidth={false}
              suffixIcon={<ArrowDownIcon style={{ pointerEvents: "none" }} />}
            >
              {productOptions.map((option) => (
                <Select.Option
                  key={option.value}
                  value={option.value}
                  style={{
                    borderRadius: "9999px",
                    padding: "4px 6px",
                    fontFamily: "Inter",
                    color: "#0e49b5",
                    backgroundColor: "#F7F9FC",
                    marginBlock: "2px",
                  }}
                >
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          );
        }

        // "+ Add Module" (relocated from the old Action column): once per user,
        // on their first row, when they still lack at least one module. Lets an
        // admin grant a module the user does not yet have.
        const isUserFirstRow =
          record.productIndex === 0 && record.roleIndex === 0;
        const moduleCount = userPool.filter(
          (u) => u.userId === record.userId
        ).length;
        // The backend emits this label lower-case ("All modules"); the column's
        // other values are Title Case, so it renders as "All Modules". Matched
        // case-insensitively so the two casings can never diverge here — this
        // is the gate on "+ Add Module", and a miss wrongly offers to add a
        // module to a user who already has all of them.
        const isAllModules = productName?.trim().toLowerCase() === "all modules";
        const canAddModule =
          isUserFirstRow &&
          moduleCount < MODULE_SCOPES.length &&
          !record.isTemporary &&
          !record.isDeactivated &&
          !isAllModules;

        return (
          <div>
            <span>{isAllModules ? "All Modules" : productName}</span>
            {canAddModule && handleAddModule && (
              <div style={{ marginTop: "2px" }}>
                <ActionButton
                  type="text"
                  onClick={() => handleAddModule(record)}
                  style={{
                    ...commonButtonStyles,
                    color: "#0e49b5",
                    paddingLeft: 0,
                  }}
                >
                  + Add Module
                </ActionButton>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Market",
      dataIndex: "markets",
      key: "market",
      width: "20rem",
      render: (markets, record) => {
        const isEditing = editingRows.has(record.key);
        const stagedMarkets = stagingData[record.key]?.markets;
        const displayMarkets = stagedMarkets || markets || [];

        if (isEditing) {
          // Region / market tree for the TreeSelect, sorted A-Z at both levels
          const treeData = Object.entries(regionMarketData)
            .map(([region, data]) => ({
              title: region,
              value: region,
              children: data.markets
                // Limit options to the caller's markets when market-scoped.
                .filter(
                  (market) =>
                    callerMarketScope === null ||
                    callerMarketScope.has(toCode(market))
                )
                .map((market) => ({
                  title: market,           // display full name
                  value: toCode(market),   // store country code
                }))
                .sort((a, b) => {
                  const titleA = (a.title || '').toLowerCase();
                  const titleB = (b.title || '').toLowerCase();
                  return titleA.localeCompare(titleB);
                }),
            }))
            // Drop regions with no in-scope markets left.
            .filter((region) => region.children.length > 0)
            .sort((a, b) => {
              const titleA = (a.title || '').toLowerCase();
              const titleB = (b.title || '').toLowerCase();
              return titleA.localeCompare(titleB);
            });

          return (
            <div>
            <ConfigProvider
              theme={{
                components: {
                  TreeSelect: { fontFamily: "Inter, sans-serif" },
                  Tree: { fontFamily: "Inter, sans-serif" },
                },
              }}
            >
              <TreeSelect
                value={Array.isArray(displayMarkets) ? displayMarkets.map(toCode) : []}
                style={{
                  width: "100%",
                  borderRadius: "9999px",
                  minHeight: "36px",
                }}
                onChange={(value) => {
                  // SHOW_CHILD returns only leaf country codes (never a region
                  // node), so selecting a country can't also select its parent
                  // region, and checking a region yields only the in-scope
                  // children the tree contains — no out-of-scope markets slip
                  // in via a region roll-up. Store the codes directly.
                  handleStageChange(record, "markets", value || []);
                }}
                styles={{
                  popup: {
                    root: {
                      borderRadius: "1.5rem",
                      padding: "0.75rem",
                      fontFamily: "Inter",
                      minWidth: "280px",
                    },
                  },
                }}
                popupMatchSelectWidth={false}
                popupRender={(menu) => {
                  // "Select all" / "Clear all" across every market in the tree
                  // (parity with the InviteUsers / scope-management modals).
                  const allCodes = Object.values(regionMarketData).flatMap((d) =>
                    d.markets.map(toCode)
                  );
                  const current = Array.isArray(displayMarkets)
                    ? displayMarkets.map(toCode)
                    : [];
                  const allSelected =
                    allCodes.length > 0 &&
                    allCodes.every((c) => current.includes(c));
                  return (
                    <>
                      <div style={{ padding: "4px 8px" }}>
                        <Button
                          type="link"
                          size="small"
                          disabled={allCodes.length === 0}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() =>
                            handleStageChange(
                              record,
                              "markets",
                              allSelected ? [] : allCodes
                            )
                          }
                          style={{ padding: 0 }}
                        >
                          {allSelected ? "Clear All" : "Select All"}
                        </Button>
                      </div>
                      {menu}
                    </>
                  );
                }}
                treeData={treeData}
                placeholder="Select markets"
                suffixIcon={<ArrowDownIcon style={{ pointerEvents: "none" }} />}
                treeCheckable={true}
                showCheckedStrategy={SHOW_CHILD}
                tagRender={(props) => {
                  const { label, onClose } = props;
                  return (
                    <Tag
                      color="#f7f9fc"
                      closable={false}
                      onClose={onClose}
                      style={{
                        height: "24px",
                        marginRight: 3,
                        border: "0.5px solid #00000010",
                        color: "#0e49b5",
                        borderRadius: "9999px",
                        display: "flex",
                        alignItems: "center",
                        fontFamily: "Inter",
                      }}
                    >
                      <div>{label}</div>
                      <div
                        className="flex items-center pl-1 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          onClose();
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <Icon component={CloseSvg} />
                      </div>
                    </Tag>
                  );
                }}
              />
            </ConfigProvider>
              <div style={{ display: "flex", gap: "12px", marginTop: "6px" }}>
                <ActionButton
                  type="text"
                  loading={isSaving}
                  onClick={async () => {
                    await handleSaveUser(record);
                    stopEditUser(record);
                  }}
                  style={{ ...commonButtonStyles, color: "#0e49b5" }}
                >
                  Save
                </ActionButton>
                <ActionButton
                  type="text"
                  disabled={isSaving}
                  onClick={() => stopEditUser(record)}
                  style={{ ...commonButtonStyles, color: "#71717a" }}
                >
                  Cancel
                </ActionButton>
              </div>
            </div>
          );
        }

        // Non-editing: the market text (unchanged) plus a per-product Edit
        // affordance relocated from the removed Action column. Edit is hidden on
        // the caller's own row and on deactivated rows, matching prior behavior.
        const showEdit =
          startEditUser &&
          !record.isDeactivated &&
          !(currentUserEmail && record.email === currentUserEmail);

        let marketDisplay;
        if (!displayMarkets || displayMarkets.length === 0) {
          marketDisplay = (
            <span className="text-gray-400">No markets assigned</span>
          );
        } else {
          const marketsArray = Array.isArray(displayMarkets)
            ? displayMarkets
            : [displayMarkets];

          // Show "All Markets" if every available market in the workspace is selected
          const allAvailableCodes = Object.values(regionMarketData).flatMap(
            (data) => data.markets.map((m) => toCode(m))
          );
          const isAllMarkets =
            allAvailableCodes.length > 0 &&
            allAvailableCodes.every((code) => marketsArray.includes(code));

          if (isAllMarkets || marketsArray[0] === "All Markets") {
            marketDisplay = <span className="text-sm">All Markets</span>;
          } else {
            marketDisplay = (
              <div className="flex flex-wrap gap-1">
                {marketsArray.map((marketName, index) => (
                  <span key={index} className="text-sm">
                    {marketName}
                    {index < marketsArray.length - 1 && "; "}
                  </span>
                ))}
              </div>
            );
          }
        }

        return (
          <div
            style={{
              display: "flex",
              // Align on the text baseline, not the box top: Edit and the market
              // label share a 14px/20px line, and baseline alignment makes their
              // glyphs sit on the same line regardless of the button's box
              // metrics. For a multi-line (wrapped) market list the container's
              // baseline is its first line, so Edit still tracks the first line.
              alignItems: "baseline",
              // Keep Edit next to the market content instead of pushed to the
              // far edge of the wide (20rem) column, where it read as detached.
              justifyContent: "flex-start",
              gap: "10px",
            }}
          >
            {marketDisplay}
            {showEdit && (
              <ActionButton
                type="text"
                onClick={() => startEditUser(record)}
                style={{
                  ...commonButtonStyles,
                  color: "#0e49b5",
                  // Collapse AntD's button box to the text's own line so it
                  // matches the plain-text market label beside it: no taller
                  // box, no vertical padding, and no 1px transparent border
                  // (which otherwise nudges the text down a pixel).
                  height: "auto",
                  padding: 0,
                  border: "none",
                  lineHeight: "20px",
                }}
              >
                Edit
              </ActionButton>
            )}
          </div>
        );
      },
    },
  ];

  return {
    columns,
  };
};
